import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { projects } from "@/data/projects";
import { BrowserFrame } from "@/components/BrowserFrame";
import { Reveal } from "@/components/Reveal";

const ProjectCard = ({ project, flip }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.14, 1, 1.08]);
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <article
      ref={ref}
      data-testid={`project-card-${project.slug}`}
      className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center"
    >
      <Reveal className={`lg:col-span-4 ${flip ? "lg:order-2" : ""}`}>
        <div className="flex items-center gap-3">
          <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500">{project.industry}</p>
          {project.concept && (
            <span className="mono text-[9px] tracking-[0.2em] uppercase text-neutral-500 border border-white/10 rounded-full px-2.5 py-1">
              Concept
            </span>
          )}
        </div>
        <h3 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-neutral-50 mt-4">
          {project.name}
        </h3>
        <p className="text-neutral-400 mt-5 leading-relaxed text-sm sm:text-base">{project.description}</p>
        <div className="flex flex-wrap gap-2 mt-6">
          {project.services.map((s) => (
            <span
              key={s}
              className="mono text-[10px] tracking-[0.15em] uppercase text-neutral-400 border border-white/10 rounded-full px-3 py-1.5"
            >
              {s}
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-6 mt-8">
          <Link
            data-testid={`project-view-${project.slug}`}
            to={`/work/${project.slug}`}
            className="group inline-flex items-center gap-2 text-neutral-100 font-medium border-b border-white/20 pb-1 hover:border-white transition-colors duration-300"
          >
            View Project
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          {project.liveUrl && (
            <a
              data-testid={`project-live-${project.slug}`}
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-neutral-400 font-medium border-b border-white/10 pb-1 hover:text-neutral-100 hover:border-white/40 transition-colors duration-300"
            >
              Visit Live Site
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          )}
        </div>
      </Reveal>

      <Reveal delay={0.15} className={`lg:col-span-8 ${flip ? "lg:order-1" : ""}`}>
        <Link to={`/work/${project.slug}`} aria-label={`View ${project.name} case study`}>
          <BrowserFrame url={project.url}>
            <div className="overflow-hidden">
              <motion.img
                src={project.images.home}
                alt={`${project.name} website homepage`}
                style={{ scale, y }}
                className="w-full h-auto"
                draggable="false"
              />
            </div>
          </BrowserFrame>
        </Link>
      </Reveal>
    </article>
  );
};

export const Work = ({ preview = false }) => (
  <section
    id="work"
    data-testid="portfolio-section"
    className={`relative px-5 sm:px-8 border-t border-white/5 ${preview ? "py-28 lg:py-40" : "pt-10 lg:pt-14 pb-28 lg:pb-40"}`}
  >
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-8 mb-14 lg:mb-20">
        <Reveal>
          <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-5">Portfolio</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-neutral-50">
            Selected Work
          </h2>
          <p className="text-neutral-400 mt-4 max-w-2xl text-sm sm:text-base leading-relaxed">
            A selection of websites we've designed for businesses across different industries.
          </p>
        </Reveal>
        {preview && (
          <Reveal delay={0.15}>
            <Link
              data-testid="work-view-all"
              to="/work"
              className="group inline-flex items-center gap-2 text-neutral-100 font-medium border-b border-white/20 pb-1 hover:border-white transition-colors duration-300"
            >
              View All Work
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        )}
      </div>

      <div className="space-y-24 lg:space-y-32">
        {projects.map((p, i) => (
          <ProjectCard key={p.slug} project={p} flip={i % 2 === 1} />
        ))}
      </div>
    </div>
  </section>
);
